/* NexaNovo Carbon Gallery — Shopify 版
   数据来自 #nx-data（Liquid 生成）；加购走 /cart/add.js；
   Full details 跳商品页前把展厅进度写进 sessionStorage，返回时原地落位。 */
import * as THREE from "three";

const DATA = JSON.parse(document.getElementById("nx-data").textContent);
const EXHIBITS = DATA.exhibits.filter(e => e.img);
const SPACING = 9, HALL_HALF = 5.2;

const stage = document.getElementById("nx-stage");
// 这一场景是彻头彻尾的填充率活：几十片全屏大小的加色透明层层叠。
// · antialias 在高 DPR 下花的正是填充率，而那时锯齿本来就看不见 —— 只在低 DPR 开。
// · powerPreference 不写，双显卡笔记本会把整个展厅丢给核显跑，帧数直接腰斩。
const renderer = new THREE.WebGLRenderer({
  antialias: devicePixelRatio < 1.5,
  powerPreference: "high-performance",
});
const DPR_CAP = Math.min(devicePixelRatio, 1.75);   // 2x 视网膜屏渲 4 倍像素，肉眼收益趋零
renderer.setPixelRatio(DPR_CAP);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
stage.appendChild(renderer.domElement);

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(58, 1, .1, 60);

// ============================================================================
//  《光柱》 — 无垠暗场里的一列光柱，产品悬浮其中
//
//  为什么推翻"美术馆走廊"：产品是平面剪照，凹龛做得越精致，越反衬它是纸板。
//  这里用两招从根上解决：
//    1. 产品始终转向相机（billboard）—— 永远看不到它的侧面，平面性消失
//    2. 环境（光柱 / 镜面地 / 微尘）高速掠过 —— 视差全部由环境承担
//  同时舍弃白盒美术馆的调性，换成更贴合空力套件的暗黑工程感。
// ============================================================================
const STATION = 19;                                   // 站距（米）
const N = EXHIBITS.length;
const RUN = N * STATION;
// 雾密度 .038：38 m 处还剩 12% 透过率，55 m 只剩 1.3%。但"看不见"不等于"不画"，
// 它们仍在视锥里，仍要老老实实画一遍 —— 一层全屏加色透明就是一次全屏填充。
// 按距离整组关掉：可见展位从 8~12 个降到 4~5 个，省掉的正是最贵的那部分。
// 取 55 不取 48：光柱是加色的，48 m 那 3.6% 亮度在纯黑场里仍可能闪一下。
const CULL = 55;

scene.fog = new THREE.FogExp2(0x04040a, .038);   // 雾浓一点，每站各自成幕，后一站不会透穿前一站
scene.background = new THREE.Color(0x04040a);

const mkTex = (draw, S = 256) => {
  const c = document.createElement("canvas"); c.width = c.height = S;
  draw(c.getContext("2d"), S);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
};

// 光柱贴图：顶端最亮，向下渐隐，边缘柔化
// 关键：上下两端都必须淡到全透明，否则圆柱的切口会露成两道硬边椭圆，
// 整根光柱瞬间变成"灯罩"。左右边缘同样淡出，柱体才没有轮廓线。
const shaftTex = mkTex((g, S) => {
  const lg = g.createLinearGradient(0, 0, 0, S);
  lg.addColorStop(0,    "rgba(0,0,0,0)");
  lg.addColorStop(.10,  "rgba(255,246,228,.78)");
  lg.addColorStop(.42,  "rgba(228,208,174,.30)");
  lg.addColorStop(.78,  "rgba(150,132,102,.07)");
  lg.addColorStop(1,    "rgba(0,0,0,0)");
  g.fillStyle = lg; g.fillRect(0, 0, S, S);
  const eg = g.createLinearGradient(0, 0, S, 0);
  eg.addColorStop(0,   "rgba(0,0,0,1)");
  eg.addColorStop(.28, "rgba(0,0,0,.25)");
  eg.addColorStop(.5,  "rgba(0,0,0,0)");
  eg.addColorStop(.72, "rgba(0,0,0,.25)");
  eg.addColorStop(1,   "rgba(0,0,0,1)");
  g.globalCompositeOperation = "destination-out";
  g.fillStyle = eg; g.fillRect(0, 0, S, S);
});

const radial = (inner, mid) => mkTex((g, S) => {
  const rg = g.createRadialGradient(S/2, S/2, 2, S/2, S/2, S/2);
  rg.addColorStop(0, inner); rg.addColorStop(.45, mid);
  rg.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = rg; g.fillRect(0, 0, S, S);
});
const poolTex  = radial("rgba(255,238,208,.95)", "rgba(255,226,182,.3)");
const dustTex  = radial("rgba(255,245,225,1)",   "rgba(255,240,215,.35)");
const haloTex  = radial("rgba(255,236,200,.55)", "rgba(255,225,180,.14)");

// 按 alpha 边界裁掉透明留白 —— 否则实物落在面片正中，四周一圈空气
function trimAlpha(img) {
  const c = document.createElement("canvas");
  c.width = img.width; c.height = img.height;
  const g = c.getContext("2d", { willReadFrequently:true });
  g.drawImage(img, 0, 0);
  let d;
  try { d = g.getImageData(0, 0, c.width, c.height).data; } catch (e) { return c; }
  let x0 = c.width, y0 = c.height, x1 = -1, y1 = -1;
  const step = Math.max(1, Math.floor(c.width / 900));
  for (let y = 0; y < c.height; y += step)
    for (let x = 0; x < c.width; x += step)
      if (d[(y * c.width + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
  if (x1 < 0) return c;
  const pad = Math.round(c.width * .004);
  x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad);
  x1 = Math.min(c.width - 1, x1 + pad); y1 = Math.min(c.height - 1, y1 + pad);
  const w = x1 - x0 + 1, h = y1 - y0 + 1;
  if (w === c.width && h === c.height) return c;
  const t = document.createElement("canvas");
  t.width = w; t.height = h;
  t.getContext("2d").drawImage(c, x0, y0, w, h, 0, 0, w, h);
  return t;
}

// ---- 镜面地 ----------------------------------------------------------------
// 真镜面要多渲染一遍场景；这里用"深色高金属地面 + 每件产品的镜像副本"
// 达到同样观感，开销为零。
const floor = new THREE.Mesh(
  new THREE.PlaneGeometry(120, RUN + 160),
  new THREE.MeshStandardMaterial({ color:0x06060a, roughness:.14, metalness:.95 })
);
floor.rotation.x = -Math.PI / 2;
floor.position.z = -RUN / 2;
scene.add(floor);

// 地面网格：纯黑虚空没有尺度也没有速度感 —— 网格线一铺，纵深、比例、
// "你在移动"三件事同时成立，而且这种工程测绘感正对空力套件的调性。
// 线材质吃雾，远处自然溶解，不需要额外淡出处理。
const grid = new THREE.GridHelper(160, 80, 0x2a3348, 0x161c2c);
grid.material.transparent = true;
grid.material.opacity = .5;
grid.material.fog = true;
grid.position.set(0, .012, -RUN / 2);
scene.add(grid);

// 远处地平辉光：给无垠感一个尽头，不然纯黑没有方向
const horizon = new THREE.Mesh(
  new THREE.PlaneGeometry(160, 26),
  new THREE.MeshBasicMaterial({ map:haloTex, transparent:true, opacity:.5,
    blending:THREE.AdditiveBlending, depthWrite:false })
);
horizon.position.set(0, 4, -RUN - 34);
scene.add(horizon);

scene.add(new THREE.AmbientLight(0x1e1e30, 1.8));
scene.add(new THREE.HemisphereLight(0x3a4058, 0x06060c, .7));

// ---- 漂浮微尘 ---------------------------------------------------------------
const DUST = 420;
const dpos = new Float32Array(DUST * 3);
const dphase = new Float32Array(DUST);
for (let i = 0; i < DUST; i++) {
  dpos[i*3]   = (Math.random() - .5) * 26;
  dpos[i*3+1] = Math.random() * 9 + .3;
  dpos[i*3+2] = -Math.random() * (RUN + 40) + 10;
  dphase[i]   = Math.random() * Math.PI * 2;
}
const dustGeo = new THREE.BufferGeometry();
dustGeo.setAttribute("position", new THREE.BufferAttribute(dpos, 3));
const dust = new THREE.Points(dustGeo, new THREE.PointsMaterial({
  map:dustTex, size:.09, transparent:true, opacity:.5, depthWrite:false,
  blending:THREE.AdditiveBlending, sizeAttenuation:true
}));
scene.add(dust);

// ---- 每一站：光柱 + 地面光池 + 产品 + 镜像 ---------------------------------
const loader = new THREE.TextureLoader();
const exhibits = [];
const billboards = [];          // 每帧转向相机的对象
const _q = new THREE.Quaternion();   // 复用，避免每帧分配（须早于渲染循环声明）

EXHIBITS.forEach((ex, i) => {
  const side = i % 2 === 0 ? -1 : 1;
  const z = -12 - i * STATION;
  const x = side * 2.6;                               // 左右错开更大，避免前后站互相穿帮
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  scene.add(g);

  // 光柱：窄口在上、开口向下的锥体。刻意让底口沉到地面以下、顶口拉得很高，
  // 两端切口都落在视野外，看到的就只有一束渐隐的光。
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(1.5, 5.2, 18, 26, 1, true),
    new THREE.MeshBasicMaterial({ map:shaftTex, transparent:true, opacity:.55,
      blending:THREE.AdditiveBlending, depthWrite:false, side:THREE.DoubleSide })
  );
  shaft.position.y = 7.6;
  g.add(shaft);

  // 柱心：更窄更亮的一束，叠出"实心光"的层次
  const core = new THREE.Mesh(
    new THREE.CylinderGeometry(.5, 2.2, 18, 18, 1, true),
    new THREE.MeshBasicMaterial({ map:shaftTex, transparent:true, opacity:.32,
      blending:THREE.AdditiveBlending, depthWrite:false, side:THREE.DoubleSide })
  );
  core.position.y = 7.6;
  g.add(core);

  // 光源本体：高处一点亮斑，光才有出处
  const src = new THREE.Mesh(
    new THREE.PlaneGeometry(2.6, 2.6),
    new THREE.MeshBasicMaterial({ map:haloTex, transparent:true, opacity:.5,
      blending:THREE.AdditiveBlending, depthWrite:false })
  );
  src.position.y = 12.4;
  g.add(src);
  billboards.push(src);

  // 落地光池
  const pool = new THREE.Mesh(
    new THREE.CircleGeometry(4.6, 32),
    new THREE.MeshBasicMaterial({ map:poolTex, transparent:true, opacity:.62,
      blending:THREE.AdditiveBlending, depthWrite:false })
  );
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = .01;
  g.add(pool);

  // 产品背后的光晕，把产品从暗场里"托"出来
  const halo = new THREE.Mesh(
    new THREE.PlaneGeometry(ex.w * 2.1, ex.w * 1.5),
    new THREE.MeshBasicMaterial({ map:haloTex, transparent:true, opacity:.42,
      blending:THREE.AdditiveBlending, depthWrite:false })
  );
  halo.position.y = 2.5;
  g.add(halo);
  billboards.push(halo);

  // 灯挂在 scene 而不是 g 上，位置直接写世界坐标。
  // 因为下面按距离整组隐藏展位 —— 灯要是在组里，可见灯数就会跟着变，
  // three 的着色器程序按灯数缓存，数一变地面材质就得重新编译一次，
  // 每过一站卡一下，比省下来的那点填充率贵得多。
  const lamp = new THREE.PointLight(0xffe9c8, 0, 16, 2);   // 强度靠近时才拉起
  lamp.position.set(x, 6.5, z);
  scene.add(lamp);

  loader.load(ex.img, tex0 => {
    const src = trimAlpha(tex0.image);
    const tex = new THREE.CanvasTexture(src);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    const W = ex.w * 2.25;                              // 放大：占据视野才有存在感
    const H = W * (src.height / src.width);
    const CY = 2.5;

    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(W, H),
      new THREE.MeshBasicMaterial({ map:tex, transparent:true })
    );
    plane.position.y = CY;
    plane.renderOrder = 3;
    g.add(plane);
    billboards.push(plane);

    // 湿地镜像：翻转 + 压扁 + 底部渐隐
    const rc = document.createElement("canvas");
    rc.width = src.width; rc.height = src.height;
    const rg = rc.getContext("2d");
    rg.drawImage(src, 0, 0);
    rg.globalCompositeOperation = "destination-in";
    const fade = rg.createLinearGradient(0, 0, 0, rc.height);
    fade.addColorStop(0, "rgba(0,0,0,.42)");
    fade.addColorStop(1, "rgba(0,0,0,0)");
    rg.fillStyle = fade; rg.fillRect(0, 0, rc.width, rc.height);
    const rtex = new THREE.CanvasTexture(rc);
    rtex.colorSpace = THREE.SRGBColorSpace;

    const mirror = new THREE.Mesh(
      new THREE.PlaneGeometry(W, H),
      new THREE.MeshBasicMaterial({ map:rtex, transparent:true, opacity:.5,
        depthWrite:false, blending:THREE.AdditiveBlending })
    );
    mirror.scale.y = -1;
    mirror.position.y = -CY;                            // 地面为镜，等距对称
    mirror.renderOrder = 2;
    g.add(mirror);
    billboards.push(mirror);
  });

  exhibits.push({ z, x, side, rotY:0, group:g, lamp, shaft, core, ...ex });
});

// ---- UI ---------------------------------------------------------------------
const $ = id => document.getElementById(id);
const rail = $("nx-rail");
EXHIBITS.forEach(() => rail.appendChild(document.createElement("i")));
const dots = [...rail.children];
const placard = $("nx-placard"), capIn = $("nx-cap-in"), capOut = $("nx-cap-out"),
      hint = $("nx-hint"), detail = $("nx-detail"), view = $("nxd-view"),
      shop = $("nx-shop");

// 展签上的「去商品页」。走之前把进度存下来，从商品页返回时落回原位 ——
// 和详情层里的 Full details 用的是同一个键，行为保持一致。
shop.addEventListener("click", () => {
  sessionStorage.setItem("nx-gallery-progress", String(progress));
});

const fmtSpecs = ex => ex.specs.map(s => {
  const [k, v] = String(s).split("|");
  return `<li>${k || ""}<b>${v || ""}</b></li>`;
}).join("");

// ---- 模式机 -----------------------------------------------------------------
let mode = "walk", activeIdx = -1, anim = null;
let progress = 0;   // 相机实际所在（每帧向 target 缓动）
let vel = 0;        // 相机速度（弹簧的状态之一，见下）
let target = 0;     // 滚动条读数
const walkLen = RUN + 26;                 // 首站前留黑场，末站后留收尾

// 二阶临界阻尼弹簧，隐式积分（任何 dt 都不会炸）。
//
// 原来是指数插值 progress += (target-progress)*(1-e^-λt)：每来一格滚轮都是
// "猛起一下 + 拖一条长尾"，格与格之间速度归零再重来 —— 滚轮本来就是离散跳变，
// 这么走就是一格一顿，看着像掉帧。弹簧多了个速度项，速度能跨格延续下去，
// 一串滚轮格子被摊成一条连续加减速的运动，这才是"阻尼"该给的东西。
//
// W 由主题编辑器的 "Camera response" 滑块给：大=跟手，小=飘。
// 直接改 progress / vel，不返回对象或数组 —— 每帧一次的分配也是分配。
const W = Math.max(2, Number(DATA.damping) || 10);
function stepSpring(dt) {
  const f = 1 + 2 * dt * W;
  const hoo = dt * W * W, hhoo = dt * hoo;
  const inv = 1 / (f + hhoo);
  const x = (f * progress + dt * vel + hhoo * target) * inv;   // 两条式子都用旧值算
  vel = (vel + hoo * (target - progress)) * inv;
  progress = x;
}
let prevT = performance.now();
const curPos = new THREE.Vector3(0, 1.7, 2);
const curLook = new THREE.Vector3(0, 1.55, -5);

// 每帧都要算，就不能每帧都 new —— 两个 Vector3 × 60fps 的垃圾足够让 GC
// 隔几秒卡一下，那一下正好被当成"掉帧"。复用同一个 pose 对象。
// 需要留存的地方（flyTo 的起止点）用 snapPose 显式拷一份。
const _pose = { near: -1, pos: new THREE.Vector3(), look: new THREE.Vector3() };
const snapPose = q => ({ pos: q.pos.clone(), look: q.look.clone() });

// 沿中线穿越，产品在两侧轻微错开 —— 你是"飞过"光柱而不是"路过"展位
function walkPose(p) {
  const camZ = 4 - p * walkLen;
  let best = -1, bestD = 1e9;
  for (let i = 0; i < exhibits.length; i++) {
    const d = Math.abs(exhibits[i].z - (camZ - 5.5));
    if (d < bestD) { bestD = d; best = i; }
  }
  const near = bestD < STATION * .46 ? best : -1;
  const e = near >= 0 ? exhibits[near] : null;
  const k = e ? 1 - Math.min(bestD / (STATION * .46), 1) : 0;
  _pose.near = near;
  // 相机往当前光柱的反方向轻微偏移，产品才不会被自己挡住
  _pose.pos.set((e ? -e.side * 1.5 * k : 0) + Math.sin(p * 21) * .1, 2.05, camZ);
  _pose.look.set((e ? e.x * k : 0), 2.35 - k * .1, camZ - 9);
  return _pose;
}

// 详情机位：正对产品退开，产品是 billboard，从哪个方向看都是正面
function detailPose(e) {
  const center = new THREE.Vector3(e.x, 2.5, e.z);
  return { pos: center.clone().add(new THREE.Vector3(0, .1, 6.2)), look: center };
}

const easeInOut = t => t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
const flyTo = (to, dur, then) => {
  anim = { from: { pos: curPos.clone(), look: curLook.clone() }, to, t0: performance.now(), dur, then };
};

// scrollHeight 是一次强制重排。原来每个 scroll 事件读一次 —— 滚轮连打时
// 一秒钟几十次重排全压在主线程上，帧就是在这里被吃掉的。缓存起来，只在
// 尺寸真的变了时重新量一次。
let maxScroll = 1;
const measureScroll = () => { maxScroll = Math.max(1, document.body.scrollHeight - innerHeight); };

addEventListener("scroll", () => {
  if (mode !== "walk") return;
  target = Math.min(1, Math.max(0, scrollY / maxScroll));
}, { passive: true });

function resize() {
  renderer.setSize(innerWidth, innerHeight, false);
  camera.aspect = innerWidth / innerHeight;
  camera.updateProjectionMatrix();
  measureScroll();
}
addEventListener("resize", resize);
addEventListener("load", measureScroll);      // 图片/字体落位后页面高度还会变
resize();

// 从商品页返回：落回离开时的位置
const saved = sessionStorage.getItem("nx-gallery-progress");
if (saved) {
  sessionStorage.removeItem("nx-gallery-progress");
  requestAnimationFrame(() => {
    measureScroll();
    const p = parseFloat(saved);
    scrollTo(0, maxScroll * p);
    progress = target = p;                 // 直接落位，别让弹簧从头把这一路重走一遍
    vel = 0;
  });
}

// ---- 详情 -------------------------------------------------------------------
// 「零件 / 装车效果」切换。只有这件展品真的有实拍照片时才出现 ——
// 没有实拍就不给这个按钮，不拿棚拍图或者合成图冒充装车效果。
function setView(mode, e) {
  const img = $("nxd-img");
  const on = mode === "fitted" && !!e.fitted;
  img.src = on ? e.fitted : e.img;
  img.alt = on ? e.name + " fitted to the car" : e.name;
  img.classList.toggle("fitted", on);
  for (const b of view.children) b.classList.toggle("sel", b.dataset.v === (on ? "fitted" : "part"));
}

view.addEventListener("click", ev => {
  const b = ev.target.closest("button");
  if (!b || activeIdx < 0) return;
  setView(b.dataset.v, exhibits[activeIdx]);
});

function enterDetail(i) {
  if (mode !== "walk" || i < 0) return;
  const e = exhibits[i];
  mode = "enter";
  document.body.style.overflow = "hidden";
  placard.classList.remove("on");
  view.classList.toggle("on", !!e.fitted);
  setView("part", e);
  if (e.fitted) new Image().src = e.fitted;      // 预热，切过去不会白一下
  $("nxd-no").textContent = String(i + 1).padStart(2, "0");
  $("nxd-name").textContent = e.name;
  $("nxd-specs").innerHTML = fmtSpecs(e);
  $("nxd-price").textContent = e.price ? e.price + "  ·  made to order" : "made to order";
  const buy = $("nxd-buy");
  buy.classList.remove("ok");
  buy.textContent = e.variant ? "Add to cart" : "Configure in editor";
  buy.disabled = !e.variant;
  $("nxd-full").style.display = e.url ? "" : "none";
  flyTo(detailPose(e), 950, () => { mode = "detail"; detail.classList.add("on"); });
}

function exitDetail() {
  if (mode !== "detail") return;
  mode = "leave";
  detail.classList.remove("on");
  flyTo(snapPose(walkPose(progress)), 850, () => {
    mode = "walk";
    document.body.style.overflow = "";
  });
}

$("nx-enter").addEventListener("click", () => enterDetail(activeIdx));
$("nxd-close").addEventListener("click", exitDetail);
addEventListener("keydown", e => { if (e.key === "Escape") exitDetail(); });

$("nxd-full").addEventListener("click", () => {
  const e = exhibits[activeIdx >= 0 ? activeIdx : 0];
  if (!e.url) return;
  sessionStorage.setItem("nx-gallery-progress", String(progress));
  location.href = e.url;
});

$("nxd-buy").addEventListener("click", async () => {
  const e = exhibits[activeIdx >= 0 ? activeIdx : 0];
  if (!e.variant) return;
  const buy = $("nxd-buy");
  buy.textContent = "Adding…";
  try {
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: e.variant, quantity: 1 }] }),
    });
    if (!r.ok) throw new Error(await r.text());
    buy.textContent = "Added ✓";
    buy.classList.add("ok");
  } catch (err) {
    buy.textContent = "Failed — retry";
    console.error(err);
  }
});

const ray = new THREE.Raycaster(), mouse = new THREE.Vector2();
renderer.domElement.addEventListener("click", ev => {
  if (mode !== "walk" || activeIdx < 0) return;
  mouse.set((ev.clientX / innerWidth) * 2 - 1, -(ev.clientY / innerHeight) * 2 + 1);
  ray.setFromCamera(mouse, camera);
  if (ray.intersectObject(exhibits[activeIdx].group, true).length) enterDetail(activeIdx);
});

// ---- 自适应分辨率 -----------------------------------------------------------
// 掉帧了就降采样，缓过来再升回去 —— 填充率吃紧时这是最直接、最有效的一档。
// 阈值取绝对值而不是"相对刷新率"：42fps 以下在任何屏幕上都是真卡，55fps 以上
// 在 60Hz 屏上就是满帧。中间那段留白，两个方向都够不着，也就不会来回抖。
const RES_DOWN = .024, RES_UP = .018;   // 秒/帧 ≈ 42fps / 55fps
let resAcc = 0, resN = 0, resHold = 0;
function autoRes(dt) {
  resAcc += dt; resN++;
  if (resAcc < .7) return;
  const avg = resAcc / resN; resAcc = 0; resN = 0;
  if (resHold > 0) { resHold--; return; }
  const cur = renderer.getPixelRatio();
  let next = cur;
  if (avg > RES_DOWN && cur > .8) next = Math.max(.8, cur - .3);
  else if (avg < RES_UP && cur < DPR_CAP) next = Math.min(DPR_CAP, cur + .25);
  if (next !== cur) {
    renderer.setPixelRatio(next);
    renderer.setSize(innerWidth, innerHeight, false);
    resHold = 2;                        // 改完先观望两个窗口，别追着自己的尾巴调
  }
}

// ---- 渲染循环 ---------------------------------------------------------------
// 原来是"没事就不画"（needsRender）。省电，但微尘、光柱自转这些环境动作
// 会跟着一起冻住 —— 停下滚轮画面就死掉，一动又活过来，观感恰恰就是"帧数低"。
// 改成常驻出帧；真正的省电交给两件事：页面不可见时整个停，长时间没人动时降到 30fps。
let lastDraw = 0, idleSince = performance.now();
let capInOn = null, capOutOn = null, hintOn = null;
// updateScene 是函数声明会被提升，循环一启动就调用它；这个计数器必须在循环之前
// 就完成初始化，否则每帧都撞 let 的暂时性死区、直接抛错，render 一次都跑不到。
let dustTick = 0;

// ---- 诊断探针 ---------------------------------------------------------------
// 加 ?nxdebug=1 才出现，客户永远看不到。
// 这台开发机的浏览器面板一隐藏，rAF 和 scroll 事件都不触发 —— 只有出帧才会走到的
// 代码在本地根本执行不了。与其猜，不如让页面自己把状态报出来。
const DBG = /[?&]nxdebug/.test(location.search);
let frames = 0, dbgBox = null, dbgErr = "";
if (DBG) {
  dbgBox = document.createElement("pre");
  dbgBox.style.cssText = "position:fixed;left:10px;top:96px;z-index:9999;margin:0;padding:10px 12px;" +
    "background:rgba(0,0,0,.82);color:#7CFC98;font:11px/1.5 ui-monospace,Consolas,monospace;" +
    "border:1px solid #2c4;border-radius:8px;white-space:pre;pointer-events:none";
  document.body.appendChild(dbgBox);
}
function dbgPaint() {
  if (!dbgBox) return;
  const vis = exhibits.filter(e => e.group.visible).length;
  const r = renderer.info.render;
  dbgBox.textContent =
    "frames   " + frames + "\n" +
    "mode     " + mode + "\n" +
    "scrollY  " + Math.round(scrollY) + " / " + maxScroll + "\n" +
    "target   " + target.toFixed(4) + "\n" +
    "progress " + progress.toFixed(4) + "\n" +
    "camZ     " + camera.position.z.toFixed(1) + "\n" +
    "exhibits " + vis + "/" + exhibits.length + " visible\n" +
    "drawcall " + r.calls + "  tris " + r.triangles + "\n" +
    "dpr      " + renderer.getPixelRatio().toFixed(2) + "\n" +
    "canvas   " + renderer.domElement.width + "x" + renderer.domElement.height +
    (dbgErr ? "\nERROR    " + dbgErr : "");
}
// 只认我们自己文件里抛的错 —— 这个店装了好几个 app 一直在报错，别算到展厅头上
addEventListener("error", e => {
  if (!/nx-gallery/.test(String(e.filename || ""))) return;
  dbgErr = String(e.message).slice(0, 70);
  dbgPaint();
});

(function loop() {
  requestAnimationFrame(loop);
  const now = performance.now();
  const dt = Math.min((now - prevT) / 1000, .1);
  prevT = now;
  frames++;
  if (DBG) dbgPaint();
  if (document.hidden) return;

  const moving = mode !== "walk" || anim ||
                 Math.abs(target - progress) > 1e-5 || Math.abs(vel) > 1e-5;
  if (moving) idleSince = now;
  // 闲置 6 秒后降到 30fps：环境动作本来就慢，看不出来
  if (!moving && now - idleSince > 6000 && now - lastDraw < 33) return;
  lastDraw = now;
  // 只在真的在动的时候采样负载：闲置降频那 30fps 不能被当成"卡"
  if (moving) autoRes(dt); else { resAcc = 0; resN = 0; }

  if (mode === "walk" && moving) {
    stepSpring(dt);
    if (Math.abs(target - progress) < 1e-5 && Math.abs(vel) < 1e-4) {
      progress = target; vel = 0;
    }
  }

  if (anim) {
    const t = Math.min((now - anim.t0) / anim.dur, 1);
    const k = easeInOut(t);
    curPos.lerpVectors(anim.from.pos, anim.to.pos, k);
    curLook.lerpVectors(anim.from.look, anim.to.look, k);
    camera.position.copy(curPos);
    camera.lookAt(curLook);
    updateScene(now);
    renderer.render(scene, camera);
    if (t >= 1) { const fn = anim.then; anim = null; fn && fn(); }
    return;
  }

  if (mode === "walk") {
    const wp = walkPose(progress);
    curPos.copy(wp.pos); curLook.copy(wp.look);
    camera.position.copy(curPos);
    camera.lookAt(curLook);

    updateScene(now);
    renderer.render(scene, camera);

    if (wp.near !== activeIdx) {
      activeIdx = wp.near;
      dots.forEach((d, i) => d.classList.toggle("on", i === wp.near));
      if (wp.near >= 0) {
        const e = exhibits[wp.near];
        $("nxp-no").textContent = String(wp.near + 1).padStart(2, "0");
        $("nxp-name").textContent = e.name;
        const s0 = String(e.specs[0] || "").split("|");
        $("nxp-specs").innerHTML = (s0[1] || s0[0] || "") +
          (e.price ? ' &nbsp;·&nbsp; <span class="price">' + e.price + "</span>" : "");
        shop.href = e.url || "#";
        shop.classList.toggle("off", !e.url);   // 没接商品的展位就不给这个入口
        placard.classList.add("on");
      } else placard.classList.remove("on");
    }
    // 这三个原来每帧都写一次 DOM —— 值没变也写，白白触发样式重算。只在翻转时写。
    const ci = progress < .04, co = progress > .965, hi = progress <= .03;
    if (ci !== capInOn)  { capIn.classList.toggle("on", ci);  capInOn = ci; }
    if (co !== capOutOn) { capOut.classList.toggle("on", co); capOutOn = co; }
    if (hi !== hintOn)   { hint.style.opacity = hi ? "" : 0;  hintOn = hi; }
  } else {
    camera.position.copy(curPos);
    camera.lookAt(curLook);
    updateScene(now);
    renderer.render(scene, camera);
  }
})();

// 每帧：产品转向相机 + 微尘漂移 + 灯随距离亮灭。
// billboard 是这版的核心 —— 面片永远正对镜头，"平面感"就无从暴露。
function updateScene(t) {
  camera.getWorldQuaternion(_q);
  for (const b of billboards) {
    b.quaternion.copy(_q);
    if (b.scale.y < 0) b.scale.y = -Math.abs(b.scale.y);   // 镜像保持翻转
  }
  // 微尘是逐点 JS 循环 + 整块顶点数据回传。飘得极慢，隔帧算一次看不出来。
  if ((dustTick ^= 1)) {
    const p = dust.geometry.attributes.position;
    const a = p.array;
    for (let i = 0; i < DUST; i++) {
      a[i*3+1] += Math.sin(t * .0004 + dphase[i]) * .0032;  // 上下浮游
      a[i*3]   += Math.cos(t * .0003 + dphase[i]) * .0024;
    }
    p.needsUpdate = true;
  }

  const camZ = camera.position.z;
  for (const e of exhibits) {
    const d = Math.abs(e.z - camZ);
    // 雾外的展位整组关掉：不进渲染队列，就没有那几层全屏透明填充
    const vis = d < CULL;
    if (e.group.visible !== vis) e.group.visible = vis;
    if (!vis) continue;

    const k = Math.max(0, 1 - d / (STATION * .9));         // 越近越亮
    e.lamp.intensity = 34 * k;
    e.shaft.material.opacity = .16 + .42 * k;
    e.core.material.opacity  = .10 + .38 * k;
    e.shaft.rotation.y += .0012;                            // 光柱缓慢自转，产生流动
    e.core.rotation.y  -= .0018;
  }
}
